import { useCallback, useMemo, useState } from 'react';

/** One toggleable column in a data table. */
export interface ColumnDef {
  /** Stable key — also the localStorage identity, so don't rename casually. */
  key: string;
  label: string;
  /** Always shown and absent from the menu (row actions, the status glyph). */
  locked?: boolean;
}

// Sibling of useColWidth's `stfors.colw.v1.<tableId>`; same per-table scoping,
// same versioned prefix so a future format change can be detected rather than
// mis-parsed.
const PREFIX = 'stfors.colvis.v1';

const storageKey = (tableId: string): string => `${PREFIX}.${tableId}`;

/** Hidden keys the user has chosen, or null when they never have. The
 *  distinction matters: "hide nothing" is a real choice, and it must not be
 *  overwritten by a default that hides something. */
function loadHidden(tableId: string): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null;
  } catch {
    return null;
  }
}

function saveHidden(tableId: string, hidden: string[]): void {
  try {
    localStorage.setItem(storageKey(tableId), JSON.stringify(hidden));
  } catch {
    /* private mode / quota — the table just won't remember */
  }
}

export interface ColVisibility {
  isVisible: (key: string) => boolean;
  toggle: (key: string) => void;
  /** Back to the table's defaults, forgetting the stored choice entirely. */
  reset: () => void;
  hiddenCount: number;
  columns: readonly ColumnDef[];
}

/**
 * Per-table column show/hide, persisted in localStorage.
 *
 * `defaultHidden` applies only until the user opens the menu and makes a
 * choice; from then on their set wins, including an empty one. A column added
 * to `columns` later is visible unless it's in `defaultHidden` AND the user has
 * no stored preference — new columns don't silently disappear for existing
 * users, which is the failure mode of storing *visible* keys instead of hidden
 * ones.
 */
export function useColVisibility(
  tableId: string,
  columns: readonly ColumnDef[],
  defaultHidden: readonly string[] = [],
): ColVisibility {
  const [hidden, setHidden] = useState<string[] | null>(() => loadHidden(tableId));

  const effective = useMemo(() => {
    const set = new Set(hidden ?? defaultHidden);
    // A locked column is never hidden, whatever is in storage.
    for (const c of columns) if (c.locked) set.delete(c.key);
    return set;
    // `defaultHidden` is typically an inline literal; keying the memo on its
    // contents rather than its identity avoids recomputing every render.
  }, [hidden, columns, defaultHidden.join(' ')]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback((key: string) => {
    setHidden((prev) => {
      const set = new Set(prev ?? defaultHidden);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      const next = [...set];
      saveHidden(tableId, next);
      return next;
    });
    // Same reasoning as above — compare contents, not identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, defaultHidden.join(' ')]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey(tableId));
    } catch {
      /* ignore */
    }
    setHidden(null);
  }, [tableId]);

  const isVisible = useCallback((key: string) => !effective.has(key), [effective]);

  return { isVisible, toggle, reset, hiddenCount: effective.size, columns };
}
